import { isAbortError } from '../abort'
import { errorAnalyticsPayload } from './errorAnalytics'
import { trackEvent } from './trackEvent'

interface ApiAnalyticsOptions<T = unknown> {
  eventName: string
  additionalProperties?: Record<string, unknown>
  getSuccessProperties?: (result: T) => Record<string, unknown>
  /**
   * Properties for a failure the product already handles, such as a known 4xx.
   * When this returns a record, the tracker emits `${eventName} - Expected` with
   * those properties and does not attach error* fields. Return undefined for an
   * unexpected failure, which still emits Failed with `errorAnalyticsPayload`.
   * Do not put error* fields in the returned record.
   */
  getExpectedFailureProperties?: (error: unknown) => Record<string, unknown> | undefined
}

export async function withAnalyticsTracking<T>(
  apiCall: () => Promise<T>,
  options: ApiAnalyticsOptions<T>,
): Promise<T> {
  const {
    eventName,
    additionalProperties = {},
    getSuccessProperties,
    getExpectedFailureProperties,
  } = options
  const startEventName = `${eventName} - Started`
  const successEventName = `${eventName} - Success`
  const cancelledEventName = `${eventName} - Canceled`
  const failureEventName = `${eventName} - Failed`
  const start = performance.now()

  try {
    // Capture start event
    trackEvent(startEventName, additionalProperties)

    const result = await apiCall()
    const duration = performance.now() - start

    // Merge additional success properties if provided
    const successProperties = getSuccessProperties ? getSuccessProperties(result) : {}

    // Capture success event
    trackEvent(successEventName, {
      ...additionalProperties,
      ...successProperties,
      duration,
    })

    return result
  } catch (error: unknown) {
    const duration = performance.now() - start

    // Abort errors are expected; track cancellation separately and rethrow
    if (isAbortError(error)) {
      trackEvent(cancelledEventName, {
        ...additionalProperties,
        duration,
      })
      throw error
    }

    const expectedFailureProperties = getExpectedFailureProperties?.(error)
    if (expectedFailureProperties) {
      trackEvent(`${eventName} - Expected`, {
        ...additionalProperties,
        ...expectedFailureProperties,
        duration,
      })
      throw error
    }

    trackEvent(failureEventName, {
      ...additionalProperties,
      ...errorAnalyticsPayload(error),
    })
    throw error
  }
}

interface StreamingAnalyticsOptions {
  eventName: string
  additionalProperties?: Record<string, unknown>
}

export interface StreamingAnalyticsTracker {
  /** Call for every text chunk that arrives from the stream. */
  onChunk: (chunk: string) => void
}

/**
 * Like `withAnalyticsTracking`, for calls that stream text. The API call gets a tracker
 * to report each chunk. Success, cancel, and failure events include the total duration,
 * the time to the first chunk, and the number of characters received.
 * The caller reports a stream failure by throwing. This function does not read the stream text.
 */
export async function withStreamingAnalyticsTracking<T>(
  apiCall: (tracker: StreamingAnalyticsTracker) => Promise<T>,
  options: StreamingAnalyticsOptions,
): Promise<T> {
  const { eventName, additionalProperties = {} } = options
  const start = performance.now()
  let timeToFirstChunk: number | null = null
  let characterCount = 0

  const tracker: StreamingAnalyticsTracker = {
    onChunk: (chunk) => {
      if (chunk.length === 0) return
      timeToFirstChunk ??= performance.now() - start
      characterCount += chunk.length
    },
  }

  const streamStats = () => ({
    duration: performance.now() - start,
    timeToFirstChunk,
    characterCount,
  })

  trackEvent(`${eventName} - Started`, additionalProperties)

  try {
    const result = await apiCall(tracker)
    trackEvent(`${eventName} - Success`, {
      ...additionalProperties,
      ...streamStats(),
    })
    return result
  } catch (error: unknown) {
    if (isAbortError(error)) {
      trackEvent(`${eventName} - Canceled`, {
        ...additionalProperties,
        ...streamStats(),
      })
      throw error
    }
    trackEvent(`${eventName} - Failed`, {
      ...additionalProperties,
      ...errorAnalyticsPayload(error),
      ...streamStats(),
    })
    throw error
  }
}
